import type {
  BackendStatus,
  ForkBlock,
} from "@gearbox-protocol/deploy-tools-shared";
import { api } from "api";
import GrafanaLink from "components/GrafanaLink";
import StatusDot from "components/StatusDot";
import { format } from "date-fns";
import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";

interface BackendBlockProps {
  executionId: string;
  chainId: number;
  backend?: BackendStatus;
  latestBlock?: ForkBlock;
}

interface SyncedBlock {
  number: number;
  time: Date;
}

/**
 * Displays the latest block indexed by the fork's backend sidecar.
 */
const BackendBlock: FC<BackendBlockProps> = props => {
  const { executionId, chainId, backend, latestBlock } = props;
  const [block, setBlock] = useState<SyncedBlock>();
  const [error, setError] = useState<string>();
  const isActive = backend?.state === "active";

  useEffect(() => {
    if (!isActive) {
      return;
    }
    api
      .backendSyncStatus(executionId, chainId)
      .then(({ data }) => {
        console.log("data", data);
        if (data.msg === "OK") {
          setBlock({ number: data.lastBlock, time: new Date(data.ts * 1000) });
        } else {
          setError(`chain ${chainId} is not supported by backend`);
        }
      })
      .catch(e => {
        setError(e instanceof Error ? e.message : `${e}`);
      });
  }, [executionId, chainId, isActive]);

  if (!backend) {
    return (
      <>
        <StatusDot status="queued" className="inline-block" />
        <span> Not enabled</span>
      </>
    );
  }

  let status: ReactNode;
  if (!isActive || error) {
    const isError = !!error || backend.state === "error";
    status = (
      <>
        <StatusDot
          status={isError ? "error" : "started"}
          className="inline-block"
        />
        <span className="ml-1">{error || backend.error || backend.state}</span>
      </>
    );
  } else if (!block) {
    status = <StatusDot status="queued" className="inline-block" />;
  } else {
    const drift = latestBlock ? block.number - latestBlock.number : 0;
    const driftStr = drift !== 0 ? ` (${drift > 0 ? "+" : ""}${drift})` : "";
    status = (
      <>
        <StatusDot status="success" className="inline-block" />
        <span>{block.number}</span>
        {!!driftStr && <span>{driftStr}</span>}
        <span className="text-slate-500">{` (${format(block.time, "HH:mm:ss dd/MM/yyyy")})`}</span>
      </>
    );
  }

  return (
    <div className="flex flex-row items-center gap-2 divide-x *:px-1 -mx-1 divide-slate-500">
      <span>{status}</span>
      {!!backend.version && <span>{`Version: ${backend.version}`}</span>}
      <GrafanaLink executionId={executionId} labels={{ role: "backend" }} json>
        Logs
      </GrafanaLink>
    </div>
  );
};

export default BackendBlock;
